import { createApi } from '@reduxjs/toolkit/query/react'
import axiosBaseQuery from '../axiosBaseQuery'
import { AuthState } from 'services/slices/auth'
import { GenericResponse } from '../types'
import { isDummyClient } from 'utils'
import i18n from 'utils/i18n'

export const authApi = createApi({
  reducerPath: 'authApi',
  tagTypes: ['Auth'],
  baseQuery: axiosBaseQuery({
    baseUrl: window.envVariables.REACT_APP_BASE_URL,
  }),
  endpoints: (builder) => ({
    getAuth: builder.query<AuthState, void>({
      query: () => ({
        url: '/v2/me/',
        method: 'GET',
      }),
      providesTags: [{ type: 'Auth' }],
    }),
    getAuthWithUser: builder.query<AuthState, string | undefined>({
      query: (id) => ({
        url: `/v2/me/?clientid=${id}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'Auth' }],
      transformResponse: (response: AuthState, meta, arg) => {
        const clientId = arg ? parseInt(arg, 10) : null
        if (!isDummyClient(clientId)) {
          return response
        }

        const sidebar = response.sidebar ? [...response.sidebar] : []

        // Handle operation module - add Route Planning Overview after routes_and_tasks
        const operationIndex = sidebar.findIndex((module) => module.id === 'operation')
        if (operationIndex !== -1) {
          const operationModule = { ...sidebar[operationIndex] }
          const operationSubMenus = operationModule.subMenus ? [...operationModule.subMenus] : []
          const hasRoutePlanningOverview = operationSubMenus.some(
            (item) => item.id === 'route_planning_overview',
          )

          if (!hasRoutePlanningOverview) {
            // Find the index of routes_and_tasks to insert after it
            const routesTasksIndex = operationSubMenus.findIndex(
              (item) => item.id === 'routes_and_tasks',
            )
            if (routesTasksIndex !== -1) {
              // Insert after routes_and_tasks
              operationSubMenus.splice(routesTasksIndex + 1, 0, {
                title: i18n.t('route_planning_overview'),
                id: 'route_planning_overview',
                link: '/ops_management/route_planning_overview/',
                redirect_react: true,
              })
            } else {
              // If routes_and_tasks not found, add at the end
              operationSubMenus.push({
                title: i18n.t('route_planning_overview'),
                id: 'route_planning_overview',
                link: '/ops_management/route_planning_overview/',
                redirect_react: true,
              })
            }

            operationModule.subMenus = operationSubMenus
            sidebar[operationIndex] = operationModule
          }

          // Add Afvalverwerking after cockpit
          const hasAfvalverwerking = operationSubMenus.some((item) => item.id === 'disposals')

          if (!hasAfvalverwerking) {
            // Find the index of cockpit to insert after it
            const cockpitIndex = operationSubMenus.findIndex((item) => item.id === 'cockpit')
            if (cockpitIndex !== -1) {
              // Insert after cockpit
              operationSubMenus.splice(cockpitIndex + 1, 0, {
                title: i18n.t('disposals'),
                id: 'disposals',
                link: '/ops_management/disposal_list/',
                redirect_react: true,
              })
            } else {
              // If cockpit not found, add at the end
              operationSubMenus.push({
                title: i18n.t('disposals'),
                id: 'disposals',
                link: '/ops_management/disposal_list/',
                redirect_react: true,
              })
            }

            operationModule.subMenus = operationSubMenus
            sidebar[operationIndex] = operationModule
          }
        }

        // Handle engagement module
        const engagementIndex = sidebar.findIndex((module) => module.id === 'engagement')
        if (engagementIndex !== -1) {
          const engagementModule = { ...sidebar[engagementIndex] }
          const engagementSubMenus = engagementModule.subMenus ? [...engagementModule.subMenus] : []
          const hasExistingMutations = engagementSubMenus.some((item) => item.id === 'mutations')

          if (!hasExistingMutations) {
            engagementSubMenus.push({
              title: 'Mutaties & Stortingen/Ledigingen',
              id: 'mutations',
              link: '/engagement/mutations/',
              redirect_react: true,
            })
            engagementModule.subMenus = engagementSubMenus
            sidebar[engagementIndex] = engagementModule
          }
        }

        // Handle asset_management module
        const assetManagementIndex = sidebar.findIndex((module) => module.id === 'asset')
        if (assetManagementIndex !== -1) {
          const assetManagementModule = { ...sidebar[assetManagementIndex] }
          const assetManagementSubMenus = assetManagementModule.subMenus
            ? [...assetManagementModule.subMenus]
            : []
          const hasExistingMutations = assetManagementSubMenus.some(
            (item) => item.id === 'rfid_chips',
          )

          if (!hasExistingMutations) {
            assetManagementSubMenus.push({
              title: 'RFID Chips',
              id: 'rfid_chips',
              link: '/asset_management/rfid_chips/',
              redirect_react: true,
            })
            assetManagementModule.subMenus = assetManagementSubMenus
            sidebar[assetManagementIndex] = assetManagementModule
          }
        }

        // Handle user module
        const userIndex = sidebar.findIndex((module) => module.id === 'user')
        if (userIndex !== -1) {
          const userModule = { ...sidebar[userIndex] }
          const userSubMenus = userModule.subMenus ? [...userModule.subMenus] : []

          // Check if subMenus already exist
          const hasUserMenu = userSubMenus.some((item) => item.id === 'user')
          const hasUserGroupMenu = userSubMenus.some((item) => item.id === 'user-group')

          if (!hasUserMenu) {
            userSubMenus.push({
              title: i18n.t('users'),
              id: 'user',
              link: '/user_management/user/',
              redirect_react: true,
            })
          }

          if (!hasUserGroupMenu) {
            userSubMenus.push({
              title: i18n.t('user_groups'),
              id: 'user-group',
              link: '/user_management/user-group/',
              redirect_react: true,
            })
          }

          userModule.subMenus = userSubMenus
          sidebar[userIndex] = userModule
        }

        //Handle fleet module, if subMenus has fleet_rfid_tags, remove it and add maintenance
        const fleetIndex = sidebar.findIndex((module) => module.id === 'fleet')
        if (fleetIndex !== -1) {
          const fleetModule = { ...sidebar[fleetIndex] }
          let fleetSubMenus = fleetModule.subMenus ? [...fleetModule.subMenus] : []
          fleetSubMenus = fleetSubMenus.filter((item) => item.id !== 'fleet_rfid_tags')

          // Add maintenance menu after activities
          const hasMaintenanceMenu = fleetSubMenus.some((item) => item.id === 'fleet_maintenance')
          if (!hasMaintenanceMenu) {
            const activitiesIndex = fleetSubMenus.findIndex(
              (item) => item.id === 'fleet_activities',
            )
            const maintenanceItem = {
              title: 'Maintenance',
              id: 'fleet_maintenance',
              link: '/fleet_management/maintenance',
              redirect_react: true,
            }

            if (activitiesIndex !== -1) {
              // Insert after activities
              fleetSubMenus.splice(activitiesIndex + 1, 0, maintenanceItem)
            } else {
              // If activities not found, add at the end
              fleetSubMenus.push(maintenanceItem)
            }
          }

          fleetModule.subMenus = fleetSubMenus
          sidebar[fleetIndex] = fleetModule
        }

        return { ...response, sidebar }
      },
    }),
    login: builder.mutation<
      {
        e360_auth: {
          access_token: string
          expires_in: number
          scope: 'profile'
          token_type: 'Bearer'
          mfa: {
            totp: boolean | null
          }
        }
      },
      { username: string; password: string; recaptchaToken?: string }
    >({
      query: ({ username, password, recaptchaToken }) => ({
        url: `/int-api/auth/login`,
        method: 'POST',
        noToken: true,
        data: {
          username,
          password,
          'g-recaptcha-response': recaptchaToken,
          platform: 'web',
        },
      }),
    }),
    forgotPassword: builder.mutation<GenericResponse, { email: string; recaptchaToken?: string }>({
      query: ({ email, recaptchaToken }) => ({
        url: `/int-api/password_recovery/`,
        method: 'POST',
        noToken: true,
        data: {
          email,
          'g-recaptcha-response': recaptchaToken,
        },
      }),
    }),
    logout: builder.mutation({
      query: () => ({
        url: '/int-api/auth/logout/',
        method: 'GET',
        withCredentials: true,
      }),
      invalidatesTags: ['Auth'],
    }),
    changePasswordRecovery: builder.mutation<
      GenericResponse,
      { new_password: string; token: string }
    >({
      query: ({ ...data }) => ({
        url: '/int-api/change_forgotten_password/',
        method: 'POST',
        noToken: true,
        data,
      }),
      invalidatesTags: ['Auth'],
    }),
  }),
})

export const {
  useGetAuthQuery,
  useGetAuthWithUserQuery,
  useLogoutMutation,
  useLoginMutation,
  useForgotPasswordMutation,
  useLazyGetAuthQuery,
  useChangePasswordRecoveryMutation,
} = authApi
